import type { ReactNode } from "react";
import { Icone, type NomeIcone } from "@/components/Icone";

/**
 * As peças de formulário do painel, na língua do design.
 *
 * Existem para que as telas de configuração não precisem repetir a mesma
 * pilha de classes — e para que, quando o traço mudar, mude num lugar só.
 * Nada aqui é biblioteca: é o mesmo desenho que o painel já tinha.
 */

/** Um bloco do formulário: título, explicação curta e os campos. */
export function Secao({
  titulo,
  ajuda,
  acao,
  children,
}: {
  titulo: string;
  ajuda?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[14px] bg-[var(--color-cartao)] p-5 shadow-cartao">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-[var(--color-tinta)]">{titulo}</h2>
          {ajuda ? <p className="mt-0.5 text-[13.5px] leading-relaxed text-[var(--color-suave)]">{ajuda}</p> : null}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

/**
 * Liga/desliga. Um `<button role="switch">` de verdade: o Tab chega nele e o
 * leitor de tela diz "ligado" ou "desligado".
 */
export function Chave({
  ligado,
  onChange,
  rotulo,
  disabled,
}: {
  ligado: boolean;
  onChange: (v: boolean) => void;
  rotulo: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      disabled={disabled}
      onClick={() => onChange(!ligado)}
      className={
        "relative h-[26px] w-11 shrink-0 rounded-full transition-colors disabled:opacity-40 " +
        (ligado ? "bg-[var(--color-marca)]" : "bg-[var(--color-borda-forte)]")
      }
    >
      <span
        className={
          "absolute top-[3px] left-[3px] h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform duration-200 " +
          (ligado ? "translate-x-[18px]" : "")
        }
      />
    </button>
  );
}

/** Um cartão simples, sem numeração. */
export function Cartao({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-[14px] bg-[var(--color-cartao)] p-5 shadow-cartao ${className ?? ""}`}>
      {children}
    </div>
  );
}

const ENTRADA =
  "w-full rounded-[10px] border border-[var(--color-borda)] bg-[var(--color-painel)] px-3 py-2 text-sm text-[var(--color-tinta)] outline-none transition-colors placeholder:text-[var(--color-fraco)] focus:border-[var(--color-marca)] focus:bg-[var(--color-cartao)] disabled:opacity-50";

export function Campo({
  rotulo,
  ajuda,
  className,
  ...props
}: { rotulo: string; ajuda?: ReactNode } & React.ComponentProps<"input">) {
  return (
    <label className="mb-3 block">
      <span className="mb-1.5 block text-[13px] font-semibold text-[var(--color-suave)]">{rotulo}</span>
      <input className={`${ENTRADA} ${className ?? ""}`} {...props} />
      {ajuda ? <span className="mt-1 block text-xs leading-relaxed text-[var(--color-fraco)]">{ajuda}</span> : null}
    </label>
  );
}

export function Area({
  rotulo,
  ajuda,
  className,
  ...props
}: { rotulo: string; ajuda?: ReactNode } & React.ComponentProps<"textarea">) {
  return (
    <label className="mb-3 block">
      <span className="mb-1.5 block text-[13px] font-semibold text-[var(--color-suave)]">{rotulo}</span>
      <textarea className={`${ENTRADA} ${className ?? ""}`} {...props} />
      {ajuda ? <span className="mt-1 block text-xs leading-relaxed text-[var(--color-fraco)]">{ajuda}</span> : null}
    </label>
  );
}

export function Escolha({
  rotulo,
  className,
  children,
  ...props
}: { rotulo: string } & React.ComponentProps<"select">) {
  return (
    <label className="mb-3 block">
      <span className="mb-1.5 block text-[13px] font-semibold text-[var(--color-suave)]">{rotulo}</span>
      <select className={`${ENTRADA} ${className ?? ""}`} {...props}>
        {children}
      </select>
    </label>
  );
}

const VARIANTES = {
  primario: "bg-[var(--color-marca)] text-[var(--color-sobre-marca)] hover:brightness-110",
  secundario:
    "bg-[var(--color-cartao)] text-[var(--color-tinta)] shadow-botao hover:bg-[var(--color-painel)]",
  contorno: "border border-[var(--color-marca)] text-[var(--color-marca)] hover:bg-[var(--color-marca)]/10",
  perigo: "text-[var(--color-alerta)] hover:bg-[var(--color-alerta)]/10",
  discreto: "text-[var(--color-suave)] hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]",
} as const;

export function Botao({
  variante = "primario",
  icone,
  className,
  children,
  ...props
}: {
  variante?: keyof typeof VARIANTES;
  icone?: NomeIcone;
} & React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      className={`flex items-center justify-center gap-1.5 h-9 rounded-full px-4 text-[13px] font-semibold transition-colors active:scale-[0.98] disabled:opacity-40 ${VARIANTES[variante]} ${className ?? ""}`}
      {...props}
    >
      {icone ? <Icone nome={icone} className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
}

const TONS = {
  alerta: { caixa: "border-transparent bg-[var(--color-alerta-fundo)] text-[var(--color-alerta)]", icone: "alerta" },
  atencao: { caixa: "border-transparent bg-[var(--color-atencao-fundo)] text-[var(--color-atencao)]", icone: "alerta" },
  info: { caixa: "border-transparent bg-[var(--color-painel)] text-[var(--color-suave)]", icone: "info" },
  bom: { caixa: "border-transparent bg-[var(--color-realce)] text-[var(--color-marca-forte)]", icone: "check" },
} as const;

/** Uma caixa de aviso. O ícone vem do tom — quem escreve não escolhe errado. */
export function Aviso({
  tom = "info",
  titulo,
  children,
}: {
  tom?: keyof typeof TONS;
  titulo?: string;
  children: ReactNode;
}) {
  const t = TONS[tom];
  return (
    <div className={`flex items-start gap-2.5 rounded-[14px] border px-4 py-3 text-xs ${t.caixa}`}>
      <Icone nome={t.icone as NomeIcone} className="mt-px h-4 w-4 shrink-0" />
      <div className="leading-relaxed">
        {titulo ? <p className="mb-1 font-semibold">{titulo}</p> : null}
        {children}
      </div>
    </div>
  );
}
